import {
  useEffect,
  useMemo,
  useState,
  type ReactNode } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  AlertCircle,
  Plus,
  Send,
  Trash2,
  Copy,
  RefreshCw,
  Plug,
  } from "lucide-react";
import logoQuickbooks from "../assets/logo-quickbooks.png";
import logoGoogleSheets from "../assets/logo-google-sheets.png";
import logoStripe from "../assets/logo-stripe.png";
import logoXero from "../assets/logo-xero.png";
import logoZapier from "../assets/logo-zapier.png";
import { usePageTitle } from "@/hooks/use-page-title";
import { ProTeaserSection } from "@/components/pro-gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card,
  CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import {
  useGetConnectStatus,
  CONNECT_STATUS_QUERY_KEY,
  useGetZapierEvents,
  useListZapierSubscriptions,
  useCreateZapierSubscription,
  useDeleteZapierSubscription,
  useTestZapierSubscription,
  type ZapierEventType,
  type ZapierSubscription,
  type ZapierEventDescriptor,
  type GoogleDriveSpreadsheetFile,
  useGetQBOStatus,
  useGetXeroStatus,
  useGetGoogleSheetsStatus,
  useListGoogleSpreadsheets,
  useSetupGoogleSheetsSync,
  useSyncGoogleSheetsNow,
  useDisconnectGoogleSheets,
  GOOGLE_SHEETS_STATUS_QUERY_KEY,
  GOOGLE_SHEETS_SPREADSHEETS_QUERY_KEY,
  type GoogleSheetsStatusConnected,
} from "@workspace/api-client-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Link as WouterLink } from "wouter";
import { cn } from "@/lib/utils";

type IntegrationCategory = string;

interface IntegrationDefinition {
  id: string;
  name: string;
  description: string;
  category: IntegrationCategory;
  icon: ReactNode;
  iconBgClass: string;
  iconColorClass: string;
}

interface IntegrationTileProps {
  integration: IntegrationDefinition;
  badge: ReactNode;
  action: ReactNode;
  extra?: ReactNode;
}

function IntegrationTile({ integration, badge, action, extra }: IntegrationTileProps) {
  return (
    <Card
      className={cn(
        "shadow-sm transition-shadow hover:shadow-md",
      )}
      data-testid={`integration-tile-${integration.id}`}
    >
      <CardContent className="p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-stretch sm:gap-6">
          <div className="flex flex-1 items-start gap-4 min-w-0">
            <div
              className={cn(
                "flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl border border-border/50",
                integration.iconBgClass,
                integration.iconColorClass,
              )}
              aria-hidden="true"
            >
              {integration.icon}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h3 className="font-semibold text-foreground leading-tight text-base">
                  {integration.name}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {integration.category}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {integration.description}
              </p>
              {extra}
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:items-end sm:justify-between sm:w-48 sm:flex-shrink-0">
            <div>{badge}</div>
            <div className="w-full sm:w-auto">{action}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const QUICKBOOKS_INTEGRATION: IntegrationDefinition = {
  id: "quickbooks",
  name: "QuickBooks Online",
  description:
    "Push invoices and estimates to QuickBooks Online on demand. The first push creates the document; later pushes update it.",
  category: "Accounting",
  icon: <img src={logoQuickbooks} alt="QuickBooks" className="w-8 h-8 object-contain" />,
  iconBgClass: "bg-white dark:bg-white/10",
  iconColorClass: "",
};

const XERO_INTEGRATION: IntegrationDefinition = {
  id: "xero",
  name: "Xero",
  description:
    "Push quotes and invoices from QuoTrak into your Xero organization. The first push creates the document; later pushes update it.",
  category: "Accounting",
  icon: <img src={logoXero} alt="Xero" className="w-8 h-8 object-contain" />,
  iconBgClass: "bg-white dark:bg-white/10",
  iconColorClass: "",
};

const STRIPE_INTEGRATION: IntegrationDefinition = {
  id: "stripe",
  name: "Stripe",
  description:
    "Accept online card payments on your invoices via Stripe Connect. Money goes straight to your account.",
  category: "Payments",
  icon: <img src={logoStripe} alt="Stripe" className="w-8 h-8 object-contain" />,
  iconBgClass: "bg-white dark:bg-white/10",
  iconColorClass: "",
};

  const ZAPIER_INTEGRATION: IntegrationDefinition = {
    id: "zapier",
    name: "Zapier",
    description:
      "Trigger Zaps when a quote is sent or accepted, an invoice is sent or paid, or a customer is created. Connects 6,000+ apps.",
    category: "Automation",
    icon: <img src={logoZapier} alt="Zapier" className="w-8 h-8 object-contain" />,
    iconBgClass: "bg-white dark:bg-white/10",
    iconColorClass: "",
  };

const GOOGLE_SHEETS_INTEGRATION: IntegrationDefinition = {
  id: "google-sheets",
  name: "Google Sheets",
  description:
    "Push your quotes, invoices, and payments into a Google Sheet you control. Click Sync Now to update your sheet whenever you're ready.",
  category: "Productivity",
  icon: <img src={logoGoogleSheets} alt="Google Sheets" className="w-8 h-8 object-contain" />,
  iconBgClass: "bg-white dark:bg-white/10",
  iconColorClass: "",
};


function StripeTile({ enabled }: { enabled: boolean }) {
  const { data: connectStatus, isLoading } = useGetConnectStatus({
    query: { queryKey: [...CONNECT_STATUS_QUERY_KEY], enabled },
  });

  let badge: ReactNode;
  let action: ReactNode;

  if (isLoading && enabled) {
    badge = (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking…
      </Badge>
    );
    action = (
      <Button variant="outline" size="sm" disabled className="w-full sm:w-auto">
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…
      </Button>
    );
  } else if (connectStatus?.connected && connectStatus.status === "active") {
    badge = (
      <Badge
        variant="outline"
        className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900"
      >
        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
      </Badge>
    );
    action = (
      <Link href="/settings?connect=manage">
        <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="button-stripe-manage">
          <ExternalLink className="w-3.5 h-3.5 mr-2" /> Manage in Settings
        </Button>
      </Link>
    );
  } else if (
    connectStatus?.connected &&
    (connectStatus.status === "pending" || connectStatus.status === "disabled")
  ) {
    badge = (
      <Badge
        variant="outline"
        className="text-xs bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-400 dark:border-yellow-900"
      >
        Setup incomplete
      </Badge>
    );
    action = (
      <Link href="/settings?connect=manage">
        <Button size="sm" className="w-full sm:w-auto" data-testid="button-stripe-finish">
          Finish setup
        </Button>
      </Link>
    );
  } else {
    badge = (
      <Badge variant="outline" className="text-xs">
        Not connected
      </Badge>
    );
    action = (
      <Link href="/settings?connect=manage">
        <Button size="sm" className="w-full sm:w-auto" data-testid="button-stripe-connect">
          Connect
        </Button>
      </Link>
    );
  }

  return <IntegrationTile integration={STRIPE_INTEGRATION} badge={badge} action={action} />;
}


function QuickBooksTile({ enabled }: { enabled: boolean }) {
  const { data: qboStatus, isLoading } = useGetQBOStatus();
  const { data: xeroStatus } = useGetXeroStatus();

  let badge: ReactNode;
  let action: ReactNode;
  let extra: ReactNode | undefined;

  const xeroConnected = xeroStatus?.connected === true;
  const qboConnected = qboStatus?.connected === true;

  if (xeroConnected && !qboConnected) {
    extra = (
      <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-2 py-1.5">
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span>Xero is connected. Disconnect it before connecting QuickBooks — only one accounting integration can be active at a time.</span>
      </div>
    );
  }

  if (!enabled) {
    badge = <Badge variant="outline" className="text-xs">Available</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled>
        Connect
      </Button>
    );
  } else if (isLoading) {
    badge = (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking…
      </Badge>
    );
    action = (
      <Button variant="outline" size="sm" disabled className="w-full sm:w-auto">
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…
      </Button>
    );
  } else if (!qboStatus?.configured) {
    badge = <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">Not configured</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled>
        Not configured
      </Button>
    );
  } else if (qboStatus?.connected && qboStatus.needsReconnect) {
    badge = (
      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900">
        <AlertCircle className="w-3 h-3 mr-1" /> Reconnect needed
      </Badge>
    );
    action = (
      <a href="/api/integrations/quickbooks/connect" className="block w-full sm:w-auto">
        <Button size="sm" className="w-full" variant="destructive" data-testid="button-qbo-reconnect">
          Reconnect
        </Button>
      </a>
    );
  } else if (qboStatus?.connected) {
    badge = (
      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
      </Badge>
    );
    action = (
      <WouterLink href="/integrations/quickbooks">
        <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="button-qbo-manage">
          <ExternalLink className="w-3.5 h-3.5 mr-2" /> Manage
        </Button>
      </WouterLink>
    );
  } else if (xeroConnected) {
    badge = <Badge variant="outline" className="text-xs">Not connected</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled data-testid="button-qbo-connect-blocked">
        Disconnect Xero first
      </Button>
    );
  } else {
    badge = <Badge variant="outline" className="text-xs">Not connected</Badge>;
    action = (
      <a href="/api/integrations/quickbooks/connect" className="block w-full sm:w-auto">
        <Button size="sm" className="w-full" data-testid="button-qbo-connect">
          Connect
        </Button>
      </a>
    );
  }

  return <IntegrationTile integration={QUICKBOOKS_INTEGRATION} badge={badge} action={action} extra={extra} />;
}

function XeroTile({ enabled }: { enabled: boolean }) {
  const { data: xeroStatus, isLoading } = useGetXeroStatus();
  const { data: qboStatus } = useGetQBOStatus();

  let badge: ReactNode;
  let action: ReactNode;
  let extra: ReactNode | undefined;

  const qboConnected = qboStatus?.connected === true;
  const xeroConnected = xeroStatus?.connected === true;

  if (qboConnected && !xeroConnected) {
    extra = (
      <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-2 py-1.5">
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span>QuickBooks Online is connected. Disconnect it before connecting Xero — only one accounting integration can be active at a time.</span>
      </div>
    );
  }

  if (!enabled) {
    badge = <Badge variant="outline" className="text-xs">Available</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled>
        Connect
      </Button>
    );
  } else if (isLoading) {
    badge = (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking…
      </Badge>
    );
    action = (
      <Button variant="outline" size="sm" disabled className="w-full sm:w-auto">
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…
      </Button>
    );
  } else if (!xeroStatus?.configured) {
    badge = <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">Not configured</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled>
        Not configured
      </Button>
    );
  } else if (xeroConnected && (xeroStatus as { needsReconnect?: boolean }).needsReconnect) {
    badge = (
      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900">
        <AlertCircle className="w-3 h-3 mr-1" /> Reconnect needed
      </Badge>
    );
    action = (
      <a href="/api/integrations/xero/connect" className="block w-full sm:w-auto">
        <Button size="sm" className="w-full" variant="destructive" data-testid="button-xero-reconnect">
          Reconnect
        </Button>
      </a>
    );
  } else if (xeroConnected) {
    badge = (
      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
      </Badge>
    );
    action = (
      <WouterLink href="/integrations/xero">
        <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="button-xero-manage">
          <ExternalLink className="w-3.5 h-3.5 mr-2" /> Manage
        </Button>
      </WouterLink>
    );
  } else if (qboConnected) {
    badge = <Badge variant="outline" className="text-xs">Not connected</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled data-testid="button-xero-connect-blocked">
        Disconnect QuickBooks first
      </Button>
    );
  } else {
    badge = <Badge variant="outline" className="text-xs">Not connected</Badge>;
    action = (
      <a href="/api/integrations/xero/connect" className="block w-full sm:w-auto">
        <Button size="sm" className="w-full" data-testid="button-xero-connect">
          Connect
        </Button>
      </a>
    );
  }

  return <IntegrationTile integration={XERO_INTEGRATION} badge={badge} action={action} extra={extra} />;
}

function ZapierManagerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { toast } = useToast();
  const eventsQuery = useGetZapierEvents({
    query: { queryKey: ["/api/integrations/zapier/events"], enabled: open },
  });
  const subsQuery = useListZapierSubscriptions({
    query: { queryKey: ["/api/integrations/zapier/subscriptions"], enabled: open },
  });
  const createMutation = useCreateZapierSubscription();
  const deleteMutation = useDeleteZapierSubscription();
  const testMutation = useTestZapierSubscription();

  const [targetUrl, setTargetUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<Set<ZapierEventType>>(new Set());
  const [createdSecret, setCreatedSecret] = useState<{ id: number; secret: string } | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [pendingTestId, setPendingTestId] = useState<number | null>(null);

  const allEvents = eventsQuery.data?.events ?? [];
  const subs = subsQuery.data?.subscriptions ?? [];

  const toggleEvent = (id: ZapierEventType) => {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetForm = () => {
    setTargetUrl("");
    setSelectedEvents(new Set());
  };

  const handleCreate = () => {
    if (!targetUrl.trim()) {
      toast({ title: "Webhook URL is required", variant: "destructive" });
      return;
    }
    if (selectedEvents.size === 0) {
      toast({ title: "Select at least one event", variant: "destructive" });
      return;
    }
    createMutation.mutate(
      { targetUrl: targetUrl.trim(), eventTypes: Array.from(selectedEvents) },
      {
        onSuccess: (data) => {
          if (data.subscription.signingSecret) {
            setCreatedSecret({ id: data.subscription.id, secret: data.subscription.signingSecret });
          }
          resetForm();
          toast({ title: "Subscription created" });
        },
        onError: (err: unknown) => {
          toast({
            title: "Failed to create subscription",
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleDelete = (id: number) => {
    setPendingDeleteId(id);
    deleteMutation.mutate(id, {
      onSettled: () => setPendingDeleteId(null),
      onSuccess: () => toast({ title: "Subscription removed" }),
      onError: (err: unknown) =>
        toast({
          title: "Failed to delete",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        }),
    });
  };

  const handleTest = (id: number) => {
    setPendingTestId(id);
    testMutation.mutate(id, {
      onSettled: () => setPendingTestId(null),
      onSuccess: (res: { ok: boolean; statusCode: number | null; error: string | null }) => {
        if (res.ok) {
          toast({ title: "Test event sent", description: `Status ${res.statusCode ?? "OK"}` });
        } else {
          toast({
            title: "Test event failed",
            description: res.error ?? `Status ${res.statusCode ?? "unknown"}`,
            variant: "destructive",
          });
        }
      },
      onError: (err: unknown) =>
        toast({
          title: "Test event failed",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        }),
    });
  };

  const copySecret = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      toast({ title: "Signing secret copied" });
    } catch {
      toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) setCreatedSecret(null); onOpenChange(next); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-zapier">
        <DialogHeader>
          <DialogTitle>Zapier webhooks</DialogTitle>
          <DialogDescription>
            In Zapier, create a Zap with the <span className="font-medium">Webhooks by Zapier — Catch Hook</span> trigger.
            Paste the URL Zapier gives you below, choose which events to send, then test from your Zap.
          </DialogDescription>
        </DialogHeader>

        {createdSecret && (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-200">
                  Save your signing secret — shown only once
                </p>
                <p className="text-amber-800 dark:text-amber-300/90 text-xs mt-1">
                  Copy this secret now and store it in your Zap. Use it to verify the
                  <code className="mx-1">X-QuoTrak-Signature</code> HMAC-SHA256 header on incoming events.
                  For security, we never display it again — if you lose it, delete this subscription
                  and create a new one to receive a fresh secret.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input value={createdSecret.secret} readOnly className="font-mono text-xs" data-testid="input-signing-secret" />
              <Button size="sm" variant="outline" onClick={() => copySecret(createdSecret.secret)} data-testid="button-copy-secret">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3 border rounded-md p-4">
          <div>
            <Label htmlFor="zapier-target-url" className="text-sm font-medium">Catch Hook URL</Label>
            <Input
              id="zapier-target-url"
              type="url"
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              data-testid="input-target-url"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Events</Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {eventsQuery.isLoading ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
                </div>
              ) : (
                allEvents.map((ev: ZapierEventDescriptor) => (
                  <label
                    key={ev.id}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                    data-testid={`event-checkbox-${ev.id}`}
                  >
                    <Checkbox
                      checked={selectedEvents.has(ev.id)}
                      onCheckedChange={() => toggleEvent(ev.id)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">{ev.label}</span>{" "}
                      <span className="text-muted-foreground">— {ev.description}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              data-testid="button-create-subscription"
            >
              {createMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5 mr-2" />
              )}
              Add subscription
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Subscriptions</h4>
          {subsQuery.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : subs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No webhook subscriptions yet.</p>
          ) : (
            <ul className="space-y-2">
              {subs.map((sub: ZapierSubscription) => (
                <ZapierSubscriptionRow
                  key={sub.id}
                  sub={sub}
                  onTest={handleTest}
                  onDelete={handleDelete}
                  isTestPending={pendingTestId === sub.id}
                  isDeletePending={pendingDeleteId === sub.id}
                />
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-close-zapier">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ZapierSubscriptionRow({
  sub,
  onTest,
  onDelete,
  isTestPending,
  isDeletePending,
}: {
  sub: ZapierSubscription;
  onTest: (id: number) => void;
  onDelete: (id: number) => void;
  isTestPending: boolean;
  isDeletePending: boolean;
}) {
  const status = sub.lastDeliveryStatus;
  const lastDeliveryLabel = useMemo(() => {
    if (!sub.lastDeliveryAt) return null;
    const d = new Date(sub.lastDeliveryAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString();
  }, [sub.lastDeliveryAt]);
  const statusBadge = useMemo(() => {
    if (status === "success") {
      return (
        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900">
          OK
        </Badge>
      );
    }
    if (status === "failed") {
      return (
        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900">
          Failing
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs">
        Idle
      </Badge>
    );
  }, [status]);

  return (
    <li className="border rounded-md p-3 space-y-2" data-testid={`zapier-sub-${sub.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-mono break-all" data-testid={`zapier-url-${sub.id}`}>{sub.targetUrl}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {sub.eventTypes.map((ev) => (
              <Badge key={ev} variant="secondary" className="text-xs">
                {ev}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {statusBadge}
          {lastDeliveryLabel && (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`zapier-last-delivery-${sub.id}`}
              title={sub.lastDeliveryAt ?? undefined}
            >
              Last delivery: {lastDeliveryLabel}
            </span>
          )}
          {sub.failureCount > 0 && (
            <span className="text-xs text-muted-foreground">{sub.failureCount} failures</span>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTest(sub.id)}
          disabled={isTestPending}
          data-testid={`button-test-${sub.id}`}
        >
          {isTestPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5 mr-2" />
          )}
          Send test
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(sub.id)}
          disabled={isDeletePending}
          data-testid={`button-delete-${sub.id}`}
        >
          {isDeletePending ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5 mr-2" />
          )}
          Remove
        </Button>
      </div>
    </li>
  );
}

function ZapierTile({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(false);
  const subsQuery = useListZapierSubscriptions({
    query: { queryKey: ["/api/integrations/zapier/subscriptions"], enabled },
  });
  const count = subsQuery.data?.subscriptions.length ?? 0;

  let badge: ReactNode;
  if (!enabled) {
    badge = (
      <Badge variant="outline" className="text-xs">
        Available
      </Badge>
    );
  } else if (subsQuery.isLoading) {
    badge = (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking…
      </Badge>
    );
  } else if (count > 0) {
    badge = (
      <Badge
        variant="outline"
        className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900"
      >
        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
      </Badge>
    );
  } else {
    badge = (
      <Badge variant="outline" className="text-xs">
        Available
      </Badge>
    );
  }

  const action = (
    <>
      <Button
        size="sm"
        className="w-full sm:w-auto"
        data-testid="button-zapier-manage"
        onClick={() => setOpen(true)}
      >
        {count > 0 ? "Manage" : "Set up"}
      </Button>
      <ZapierManagerDialog open={open} onOpenChange={setOpen} />
    </>
  );

  return <IntegrationTile integration={ZAPIER_INTEGRATION} badge={badge} action={action} />;
}

function GoogleSheetsTile({
  enabled,
  openSetupSignal,
  onSetupOpenedConsumed,
}: {
  enabled: boolean;
  openSetupSignal: number;
  onSetupOpenedConsumed: () => void;
}) {
  const { data: status, isLoading } = useGetGoogleSheetsStatus({
    query: { queryKey: [...GOOGLE_SHEETS_STATUS_QUERY_KEY], enabled },
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const syncNowMutation = useSyncGoogleSheetsNow();

  const handleSyncNow = () => {
    syncNowMutation.mutate(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [...GOOGLE_SHEETS_STATUS_QUERY_KEY] });
        toast({ title: "Sync started" });
      },
      onError: (err: unknown) =>
        toast({
          title: "Failed to start sync",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        }),
    });
  };

  const connected = status?.connected === true ? (status as GoogleSheetsStatusConnected) : null;
  const setupNeeded = !!connected && !connected.setupCompleted;

  useEffect(() => {
    if (openSetupSignal === 0) return;
    if (isLoading) return;
    if (setupNeeded) {
      setDialogOpen(true);
    }
    onSetupOpenedConsumed();
  }, [openSetupSignal, isLoading, setupNeeded, onSetupOpenedConsumed]);

  let badge: ReactNode;
  let action: ReactNode;

  if (!enabled) {
    badge = <Badge variant="outline" className="text-xs">Available</Badge>;
    action = (
      <Button variant="outline" size="sm" className="w-full sm:w-auto opacity-60" disabled>
        Connect
      </Button>
    );
  } else if (isLoading) {
    badge = (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking…
      </Badge>
    );
    action = (
      <Button variant="outline" size="sm" disabled className="w-full sm:w-auto">
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…
      </Button>
    );
  } else if (!connected) {
    badge = <Badge variant="outline" className="text-xs">Not connected</Badge>;
    action = (
      <a href="/api/integrations/google-sheets/connect" className="block w-full sm:w-auto">
        <Button size="sm" className="w-full" data-testid="button-google-sheets-connect">
          Connect
        </Button>
      </a>
    );
  } else if (!connected.setupCompleted) {
    badge = (
      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-400 dark:border-yellow-900">
        Setup incomplete
      </Badge>
    );
    action = (
      <Button
        size="sm"
        className="w-full sm:w-auto"
        onClick={() => setDialogOpen(true)}
        data-testid="button-google-sheets-finish-setup"
      >
        Finish setup
      </Button>
    );
  } else if (connected.backfillStatus === "queued" || connected.backfillStatus === "running") {
    badge = (
      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Backfilling…
      </Badge>
    );
    action = (
      <Button
        variant="outline"
        size="sm"
        className="w-full sm:w-auto"
        onClick={() => setDialogOpen(true)}
        data-testid="button-google-sheets-manage"
      >
        Manage
      </Button>
    );
  } else if (connected.lastSyncStatus === "failed" || connected.lastSyncStatus === "errored") {
    badge = (
      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900">
        <AlertCircle className="w-3 h-3 mr-1" /> Last sync failed
      </Badge>
    );
    action = (
      <div className="flex gap-2 w-full sm:w-auto">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 sm:flex-none"
          onClick={handleSyncNow}
          disabled={syncNowMutation.isPending}
          data-testid="button-google-sheets-sync-now"
        >
          {syncNowMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
          )}
          Sync Now
        </Button>
        <Button
          size="sm"
          className="flex-1 sm:flex-none"
          onClick={() => setDialogOpen(true)}
          data-testid="button-google-sheets-manage"
        >
          Manage
        </Button>
      </div>
    );
  } else {
    badge = (
      <Badge
        variant="outline"
        className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900"
      >
        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
      </Badge>
    );
    action = (
      <div className="flex gap-2 w-full sm:w-auto">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 sm:flex-none"
          onClick={handleSyncNow}
          disabled={syncNowMutation.isPending}
          data-testid="button-google-sheets-sync-now"
        >
          {syncNowMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
          )}
          Sync Now
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 sm:flex-none"
          onClick={() => setDialogOpen(true)}
          data-testid="button-google-sheets-manage"
        >
          Manage
        </Button>
      </div>
    );
  }

  let extra: ReactNode;
  if (connected?.setupCompleted) {
    extra = (
      <div className="space-y-1 text-xs text-muted-foreground">
        {connected.spreadsheetUrl && connected.spreadsheetName ? (
          <p>
            Spreadsheet:{" "}
            <a
              href={connected.spreadsheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium text-foreground inline-flex items-center gap-1"
              data-testid="link-google-sheets-spreadsheet"
            >
              {connected.spreadsheetName}
              <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        ) : null}
        {connected.lastSyncAt ? (
          <p>Last sync: {new Date(connected.lastSyncAt).toLocaleString()}</p>
        ) : null}
        {connected.lastSyncError &&
        (connected.lastSyncStatus === "failed" || connected.lastSyncStatus === "errored") ? (
          <p className="text-red-700 dark:text-red-400">{connected.lastSyncError}</p>
        ) : null}
      </div>
    );
  } else if (connected) {
    extra = (
      <p className="text-xs text-muted-foreground">
        Connected as {connected.googleEmail}. Finish setup to start syncing.
      </p>
    );
  }

  return (
    <>
      <IntegrationTile integration={GOOGLE_SHEETS_INTEGRATION} badge={badge} action={action} extra={extra} />
      {connected ? (
        <GoogleSheetsManagerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          status={connected}
        />
      ) : null}
    </>
  );
}

function GoogleSheetsManagerDialog({
  open,
  onOpenChange,
  status,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  status: GoogleSheetsStatusConnected;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<"create" | "existing">(
    status.spreadsheetId ? "existing" : "create",
  );
  const [title, setTitle] = useState("");
  const [selectedId, setSelectedId] = useState<string>(status.spreadsheetId ?? "");
  const [search, setSearch] = useState("");
  const [syncQuotes, setSyncQuotes] = useState<boolean>(status.syncQuotes ?? true);
  const [syncInvoices, setSyncInvoices] = useState<boolean>(status.syncInvoices ?? true);
  const [syncPayments, setSyncPayments] = useState<boolean>(status.syncPayments ?? true);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(status.spreadsheetId ? "existing" : "create");
      setSelectedId(status.spreadsheetId ?? "");
      setTitle("");
      setSearch("");
      setSyncQuotes(status.syncQuotes ?? true);
      setSyncInvoices(status.syncInvoices ?? true);
      setSyncPayments(status.syncPayments ?? true);
    }
  }, [open, status.spreadsheetId, status.syncQuotes, status.syncInvoices, status.syncPayments]);

  const filesQuery = useListGoogleSpreadsheets({
    query: {
      queryKey: [...GOOGLE_SHEETS_SPREADSHEETS_QUERY_KEY],
      enabled: open && mode === "existing",
    },
  });

  const setupMutation = useSetupGoogleSheetsSync();
  const syncNowMutation = useSyncGoogleSheetsNow();
  const disconnectMutation = useDisconnectGoogleSheets();

  const invalidateStatus = () =>
    queryClient.invalidateQueries({ queryKey: [...GOOGLE_SHEETS_STATUS_QUERY_KEY] });

  const filteredFiles = useMemo(() => {
    const files = filesQuery.data?.files ?? [];
    if (!search.trim()) return files;
    const q = search.trim().toLowerCase();
    return files.filter((f: GoogleDriveSpreadsheetFile) => f.name.toLowerCase().includes(q));
  }, [filesQuery.data?.files, search]);

  const handleSave = () => {
    if (!syncQuotes && !syncInvoices && !syncPayments) {
      toast({ title: "Pick at least one entity to sync", variant: "destructive" });
      return;
    }
    if (mode === "existing" && !selectedId) {
      toast({ title: "Select a spreadsheet", variant: "destructive" });
      return;
    }
    setupMutation.mutate(
      {
        mode,
        spreadsheetId: mode === "existing" ? selectedId : undefined,
        title: mode === "create" ? title.trim() || undefined : undefined,
        syncQuotes,
        syncInvoices,
        syncPayments,
      },
      {
        onSuccess: () => {
          invalidateStatus();
          toast({ title: "Sync configured", description: "We're backfilling your sheet now." });
          onOpenChange(false);
        },
        onError: (err: unknown) =>
          toast({
            title: "Failed to set up sync",
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          }),
      },
    );
  };

  const handleSyncNow = () => {
    syncNowMutation.mutate(undefined, {
      onSuccess: () => {
        invalidateStatus();
        toast({ title: "Sync started" });
      },
      onError: (err: unknown) =>
        toast({
          title: "Failed to start sync",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        }),
    });
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        invalidateStatus();
        toast({ title: "Google Sheets disconnected" });
        setConfirmDisconnect(false);
        onOpenChange(false);
      },
      onError: (err: unknown) =>
        toast({
          title: "Failed to disconnect",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        }),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-google-sheets">
          <DialogHeader>
            <DialogTitle>Google Sheets sync</DialogTitle>
            <DialogDescription>
              Connected as <span className="font-medium">{status.googleEmail}</span>. Pick a spreadsheet
              and choose what to sync. Use <span className="font-medium">Sync Now</span> to push your latest data to Google Sheets.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Spreadsheet</Label>
              <RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as "create" | "existing")}
                className="flex flex-col gap-2"
              >
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="create" className="mt-0.5" data-testid="radio-mode-create" />
                  <span>
                    <span className="font-medium">Create a new spreadsheet</span>
                    <span className="block text-muted-foreground text-xs">
                      We'll create one in your Google Drive.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="existing" className="mt-0.5" data-testid="radio-mode-existing" />
                  <span>
                    <span className="font-medium">Use an existing spreadsheet</span>
                    <span className="block text-muted-foreground text-xs">
                      Pick one from your Drive.
                    </span>
                  </span>
                </label>
              </RadioGroup>

              {mode === "create" ? (
                <div>
                  <Label htmlFor="gsheets-title" className="text-sm">Title (optional)</Label>
                  <Input
                    id="gsheets-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="QuoTrak — My business"
                    data-testid="input-gsheets-title"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Search your spreadsheets…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="input-gsheets-search"
                  />
                  <div className="border rounded-md max-h-56 overflow-y-auto">
                    {filesQuery.isLoading ? (
                      <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                      </div>
                    ) : filesQuery.isError ? (
                      <div className="p-3 text-sm text-red-700 dark:text-red-400">
                        Failed to load spreadsheets.
                      </div>
                    ) : filteredFiles.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">No spreadsheets found.</div>
                    ) : (
                      <ul className="divide-y">
                        {filteredFiles.map((f: GoogleDriveSpreadsheetFile) => (
                          <li key={f.id}>
                            <label className="flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/50 text-sm">
                              <input
                                type="radio"
                                name="gsheets-file"
                                checked={selectedId === f.id}
                                onChange={() => setSelectedId(f.id)}
                                data-testid={`radio-file-${f.id}`}
                              />
                              <span className="flex-1 min-w-0 truncate">{f.name}</span>
                              {f.webViewLink ? (
                                <a
                                  href={f.webViewLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              ) : null}
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">What to sync</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={syncQuotes}
                    onCheckedChange={(v) => setSyncQuotes(v === true)}
                    data-testid="checkbox-sync-quotes"
                  />
                  Quotes
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={syncInvoices}
                    onCheckedChange={(v) => setSyncInvoices(v === true)}
                    data-testid="checkbox-sync-invoices"
                  />
                  Invoices
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={syncPayments}
                    onCheckedChange={(v) => setSyncPayments(v === true)}
                    data-testid="checkbox-sync-payments"
                  />
                  Payments
                </label>
              </div>
            </div>

            {status.setupCompleted ? (
              <div className="border-t pt-4 space-y-2">
                <h4 className="text-sm font-semibold">Sync</h4>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncNow}
                    disabled={syncNowMutation.isPending}
                    data-testid="button-sync-now"
                  >
                    {syncNowMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5 mr-2" />
                    )}
                    Sync now
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDisconnect(true)}
                    data-testid="button-disconnect"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-2" />
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-close-google-sheets">
              Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={setupMutation.isPending}
              data-testid="button-save-google-sheets"
            >
              {setupMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Sheets?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll stop syncing to your spreadsheet and remove your saved Google credentials. The
              spreadsheet itself stays in your Drive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-disconnect">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              data-testid="button-confirm-disconnect"
            >
              {disconnectMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
              ) : null}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function IntegrationsCatalog({
  activeEnabled,
  openSetupSignal,
  onSetupOpenedConsumed,
}: {
  activeEnabled: boolean;
  openSetupSignal: number;
  onSetupOpenedConsumed: () => void;
}) {
  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Active</h2>
          <p className="text-sm text-muted-foreground">
            Integrations you can use right now.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <StripeTile enabled={activeEnabled} />
          <QuickBooksTile enabled={activeEnabled} />
          <XeroTile enabled={activeEnabled} />
          <ZapierTile enabled={activeEnabled} />
          <GoogleSheetsTile
            enabled={activeEnabled}
            openSetupSignal={openSetupSignal}
            onSetupOpenedConsumed={onSetupOpenedConsumed}
          />
        </div>
      </section>

    </div>
  );
}

export default function IntegrationsPage() {
  usePageTitle("Integrations");
  const { user } = useAuth();
  const { toast } = useToast();
  const { hasProAccess: isPro } = getTrialInfo(user);
  const [openSetupSignal, setOpenSetupSignal] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xero = params.get("xero");
    if (xero) {
      if (xero === "connected") {
        toast({ title: "Xero connected", description: "You can now push quotes and invoices to Xero." });
      } else if (xero === "error") {
        const reason = params.get("reason") ?? "Could not connect to Xero.";
        toast({ title: "Xero connection failed", description: reason, variant: "destructive" });
      } else if (xero === "not_authenticated") {
        toast({ title: "Sign in required", description: "Please sign in and try again.", variant: "destructive" });
      }
      params.delete("xero");
      params.delete("reason");
      const search = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (search ? `?${search}` : ""));
    }
    const gsheets = params.get("gsheets");
    if (!gsheets) return;
    if (gsheets === "connected") {
      toast({ title: "Google connected", description: "Pick a spreadsheet to finish setup." });
      setOpenSetupSignal((n) => n + 1);
    } else {
      const messages: Record<string, string> = {
        cancelled: "Connection canceled.",
        not_authenticated: "Please sign in and try again.",
        state_missing: "Security check failed. Please try connecting again.",
        state_mismatch: "Security check failed. Please try connecting again.",
        no_email: "We couldn't read your Google email. Please try again.",
        no_refresh_token: "Google didn't send a refresh token. Try again from a fresh browser session.",
        token_exchange_failed: "Couldn't complete the Google sign-in. Please try again.",
      };
      toast({
        title: "Google Sheets connection failed",
        description: messages[gsheets] ?? gsheets,
        variant: "destructive",
      });
    }
    params.delete("gsheets");
    const search = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (search ? `?${search}` : ""));
  }, [toast]);

  const handleSetupOpenedConsumed = () => setOpenSetupSignal(0);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Plug className="w-8 h-8 text-primary" />
          Integrations
        </h1>
        <p className="text-muted-foreground mt-1">
          Connect QuoTrak to the tools you already use.
        </p>
      </div>

      {isPro ? (
        <IntegrationsCatalog
          activeEnabled
          openSetupSignal={openSetupSignal}
          onSetupOpenedConsumed={handleSetupOpenedConsumed}
        />
      ) : (
        <ProTeaserSection
          title="Integrations"
          description="Connect QuoTrak to QuickBooks, Xero, Google Sheets, Zapier, and more to automate your workflow."
        >
          <IntegrationsCatalog
            activeEnabled={false}
            openSetupSignal={openSetupSignal}
            onSetupOpenedConsumed={handleSetupOpenedConsumed}
          />
        </ProTeaserSection>
      )}
    </div>
  );
}
